import { Link } from '@tanstack/react-router';
import type { ArticleStatus, AuthorDto } from '@zoxo/shared';
import { Newspaper, Plus } from '@/components/icons';

import { Button } from '../../components/ui/button';
import { PILL_TAB, PILL_TAB_OFF, PILL_TAB_ON } from '../../components/ui/pill-tab';
import { cn } from '../../lib/utils';
import { EmptyState } from '../../components/ui/empty-state';
import { useIntersection } from '../../hooks/use-intersection';
import { useAuth } from '../../lib/auth';
import { formatDate } from './format';
import { useArticles } from './use-articles';
import { useMyArticles } from './use-my-articles';
import { usePendingArticles } from './use-admin-articles';
import { useMe } from '../auth/use-me';
import { ListTail } from '../../components/ui/list-tail';

/**
 * 좋소 PD 수첩 blog list (V4 §C). Editorial periodical tone.
 *
 * - Blog-style cards: first-body-image thumbnail (text-only fallback when null),
 *   title, author (nickname + company initials), plain-text summary, date, tags.
 * - The feed is useInfiniteQuery (infinite-scroll exception, not Suspense) with
 *   an IntersectionObserver sentinel for the next cursor page.
 * - Only approved (isActive) articles are returned (server-enforced).
 */
/**
 * 목록 머리는 제목·부제 대신 **탭 두 개**다: 공개(발행됨) | 비공개(초안·검수 대기).
 * 어디에 있는지는 머리의 최상위 메뉴(아티클 알약)가 이미 말한다.
 * 비공개 탭은 로그인한 사람에게만 — 내 초안·검수 대기, 관리자면 검수 대기 큐 전부.
 * 익명은 공개 탭 하나뿐이다 — 서버는 항상 비로그인으로 그린다.
 */
type View = 'public' | 'private';

/** 공개(`/articles`) | 비공개(`/articles/pending`). 탭은 주소다 — 상태가 아니다 (2026-09-24 형이 정한 트리). */
export function ArticleList({ tag, view = 'public' }: { tag?: string; view?: View }) {
  const { isAuthenticated } = useAuth();
  const showPrivate = isAuthenticated && view === 'private';
  return (
    <section>
      <h1 className="sr-only">아티클</h1>
      <div className="mb-4 flex items-center justify-between gap-2">
        <div role="tablist" aria-label="아티클 보기" className="flex items-center gap-1">
          <Link to="/articles" role="tab" aria-selected={!showPrivate} className={`${PILL_TAB} ${showPrivate ? PILL_TAB_OFF : PILL_TAB_ON}`}>
            공개
          </Link>
          {isAuthenticated ? (
            <Link to="/articles/pending" role="tab" aria-selected={showPrivate} className={`${PILL_TAB} ${showPrivate ? PILL_TAB_ON : PILL_TAB_OFF}`}>
              비공개
            </Link>
          ) : null}
        </div>
        {/* 커뮤니티의 「+ 글쓰기」와 같은 클래스(PILL_TAB + 초록). 손으로 쓰면 테두리 1px 만큼 크기가 어긋난다. */}
        <Link
          to="/articles/create"
          className={cn(PILL_TAB, 'shrink-0 gap-1 bg-brand text-white hover:bg-brand-hover')}
        >
          <Plus aria-hidden="true" className="size-4" />
          글쓰기
        </Link>
      </div>
      {showPrivate ? <PrivateArticles /> : <ArticleFeed tag={tag} />}
    </section>
  );
}

/**
 * 비공개 탭: 초안·검수 대기.
 *   - 내 글: /articles/mine 에서 isActive=false 인 것 (초안 + 검수 대기).
 *   - 관리자: 거기에 /articles/pending (모든 작성자의 검수 대기 큐)을 합친다. 같은 글은 한 번만.
 * 관리자 큐 항목은 목록 DTO 가 얇아(제목·작성자·시각) 카드가 요약·태그 없이 그려진다.
 */
function PrivateArticles() {
  const { data: me } = useMe();
  const isAdmin = me?.role === 'admin';
  const mine = useMyArticles();
  const queue = usePendingArticles();
  // 관리자만 큐를 부른다 — 일반 회원이 부르면 403 이 돌아와 오류 UI 가 뜬다.
  const queueItems = isAdmin ? (queue.data ?? []) : [];

  if (mine.isPending) return <p className="text-sm text-text-muted">불러오는 중…</p>;
  if (mine.isError) {
    return (
      <p role="alert" className="py-8 text-center text-sm text-text">
        내 기사를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.
      </p>
    );
  }

  const own: CardItem[] = mine.data.pages
    .flatMap((p) => p.data)
    .filter((a) => !a.isActive)
    .map((a) => ({ ...a, editable: true }));
  const seen = new Set(own.map((a) => a.id));
  const others: CardItem[] = queueItems
    .filter((q) => !seen.has(q.id))
    .map((q) => ({
      id: q.id, title: q.title, thumbnailUrl: null, summary: '', createdAt: q.createdAt,
      author: q.author, status: 'pending', editable: true,
    }));
  const items = [...own, ...others];

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Newspaper />}
        title="비공개 기사가 없어요."
        description="초안이나 검수 대기 중인 기사가 여기에 보여요."
      />
    );
  }
  return (
    <ul className="divide-y divide-border-subtle">
      {items.map((article) => (
        <ArticleCard key={article.id} article={article} />
      ))}
    </ul>
  );
}

function ArticleFeed({ tag }: { tag?: string }) {
  const { data, error, isPending, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useArticles(tag);

  const sentinelRef = useIntersection(() => fetchNextPage(), hasNextPage && !isFetchingNextPage);

  if (isPending) {
    return <p className="text-sm text-text-muted">불러오는 중…</p>;
  }

  if (isError) {
    return (
      <div role="alert" className="py-8 text-center">
        <p className="mb-4 text-sm text-text">
          기사를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.
        </p>
        <Button variant="secondary" onClick={() => void fetchNextPage()}>
          다시 시도
        </Button>
        <span hidden>{error.message}</span>
      </div>
    );
  }

  const articles = data.pages.flatMap((p) => p.data);

  if (articles.length === 0) {
    return (
      <EmptyState
        icon={<Newspaper />}
        title="아직 발행된 기사가 없어요."
        description="우리 일터에서 짚어야 할 이야기가 있다면, 전지적 좋소 시점에 제보해 보세요."
        action={
          <Button asChild variant="primary">
            <Link to="/articles/create">글쓰기</Link>
          </Button>
        }
      />
    );
  }

  return (
    <>
      <ul className="divide-y divide-border-subtle">
        {articles.map((article) => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </ul>
      <div ref={sentinelRef} aria-hidden="true" />
      <ListTail fetching={isFetchingNextPage} done={!hasNextPage} />
    </>
  );
}

/** 공개·비공개 탭이 같은 카드를 쓴다. 비공개 항목은 제목 앞 상태 알약 + 태그 아래 「수정」. */
interface CardItem {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  summary: string;
  tags?: string[];
  createdAt: string;
  author?: AuthorDto;
  /** 'draft' | 'pending' 이면 제목 앞에 알약. 발행됨은 알약 없음. */
  status?: ArticleStatus;
  editable?: boolean;
}

const STATUS_PILL: Partial<Record<ArticleStatus, string>> = { draft: '초안', pending: '검수 대기' };

function ArticleCard({ article }: { article: CardItem }) {
  const pill = article.status ? STATUS_PILL[article.status] : undefined;
  /*
    카드 전체를 <a> 로 감싸지 않는다 — 「수정」이 태그 줄 아래 본문 열 안에 있어야 하는데
    <a> 안에 <a> 는 안 된다. 그림과 제목이 상세로 가는 링크다.
  */
  return (
    <li className="flex gap-3 py-4">
      {article.thumbnailUrl && (
        <Link
          to="/articles/$id"
          params={{ id: article.id }}
          className="group aspect-[4/3] w-1/3 shrink-0 overflow-hidden rounded-md bg-surface-muted"
        >
          <img
            src={article.thumbnailUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </Link>
      )}
      <div className="min-w-0 flex-1">
        <h2 className="mb-1 line-clamp-2 text-base font-bold leading-snug text-text">
          {pill ? (
            <span className="mr-1.5 inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 align-middle text-xs font-medium text-gray-600">
              {pill}
            </span>
          ) : null}
          <Link to="/articles/$id" params={{ id: article.id }} className="hover:underline underline-offset-2">
            {article.title}
          </Link>
        </h2>
        {article.summary && (
          <p className="mb-1 line-clamp-2 text-sm leading-relaxed text-text-muted">
            {article.summary}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-text-subtle">
          {article.author ? (
            <>
              {/* 닉네임만 — 실명·식별자 없음. */}
              <span className="font-medium text-text-muted">{article.author.nickname}</span>
              <span aria-hidden="true">·</span>
            </>
          ) : null}
          <span>{formatDate(article.createdAt)}</span>
        </div>
        {article.tags && article.tags.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {article.tags.map((t) => (
              <li
                key={t}
                className="rounded-md bg-brand-soft px-2 py-0.5 text-xs font-medium text-brand-strong"
              >
                #{t}
              </li>
            ))}
          </ul>
        )}
        {article.editable ? (
          <div className="mt-2">
            <Link
              to="/articles/$id/edit"
              params={{ id: article.id }}
              className="text-xs font-medium text-text-muted hover:text-text hover:underline underline-offset-2"
            >
              수정
            </Link>
          </div>
        ) : null}
      </div>
    </li>
  );
}
